import { FC, ReactNode, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '../../../../../../redux/store';
import styles from './styles.module.css';
import ModalFooter from '../ModalFooter';
import {
  removeEditTask,
  setEditTask,
  calcTasksReadyToUpdate,
} from '../../../../../../redux/carAllocationEditSlice';
import TimelineHead from '../../TimelineHead';
import classNames from 'classnames';
import { parseTime } from '../../../../../../services/parseTime';
import { generateSequentialNumbers } from '../../../../../../services/generateSequentialNumbers';
import TimeCellData from '../../TimeCellData';
import { AllocationTask } from '../../allocationTableUtility';
import { addMinutes } from 'date-fns';
import { getWorkCategoryIDByName } from '../../../../../../services/workCategory';

const times = generateSequentialNumbers(0, 23).flatMap((time) => [
  `${time}:00`,
  `${time}:30`,
]);
// 型補正のためのゼロ値: TaskAllocated
const zeroValueAllocatedTask: AllocationTask = {
  type: 'allocated',
  contractId: 0,
  contractDetailId: 0,
  wasteItemId: 0,
  carAllocationId: 0,
  goesStraight: false,
  workCategory: {
    id: 0,
    estimatedTime: 0,
    name: '',
  },
  startDate: '',
  startDateForPlacement: '',
  endDate: '',
  endDateForPlacement: '',
  isLocked: false,
  carId: undefined,
  userId: undefined,
  isEditable: {
    vertical: true,
    horizontal: true,
  },
  isMergeTask: false,
};
// 型補正のためのゼロ値: TaskUnallocated
const zeroValueUnAllocatedTask: AllocationTask = {
  type: 'unallocated',
  contractId: 0,
  contractDetailId: 0,
  wasteItemId: 0,
  goesStraight: false,
  workCategory: {
    id: 0,
    estimatedTime: 0,
    name: '',
  },
  isLocked: false,
  startDate: '',
  startDateForPlacement: '',
  endDate: '',
  endDateForPlacement: '',
  carId: undefined,
  userId: undefined,
  isEditable: {
    vertical: true,
    horizontal: true,
  },
  isMergeTask: false,
};
const merge = <T extends object>(base: T, ...appendItems: Partial<T>[]): T => {
  return Object.assign({ ...base }, ...appendItems);
};
const AllocatePairTask: FC = () => {
  const { editState, allocationTasks } = useSelector(
    (state: AppState) => state.carAllocationEdit
  );
  const dispatch = useDispatch<AppDispatch>();
  const [selectedTime, setSelectedTime] = useState<Date | null>(
    editState?.secondary.startDate
      ? new Date(editState.secondary.startDate)
      : null
  );
  if (!editState?.primary.startDate || !editState?.primary.endDate) {
    return null;
  }
  const zeroValueAllocation =
    editState.primary.type === 'allocated'
      ? zeroValueAllocatedTask
      : zeroValueUnAllocatedTask;
  const pairedAllocation = selectedTime
    ? merge(zeroValueAllocation, editState.secondary, {
        startDate: selectedTime.toISOString(),
        startDateForPlacement: selectedTime.toISOString(),
        endDate: addMinutes(
          selectedTime,
          editState.secondary.workCategory?.estimatedTime || 0
        ).toISOString(),
        endDateForPlacement: addMinutes(
          selectedTime,
          editState.secondary.workCategory?.estimatedTime || 0
        ).toISOString(),
      })
    : merge(zeroValueAllocation, editState.secondary, {
        startDate: undefined,
        startDateForPlacement: undefined,
        endDate: undefined,
        endDateForPlacement: undefined,
      });
  const allocations = [
    // 優先して find されるように
    merge(zeroValueAllocation, editState.primary),
    ...(pairedAllocation ? [pairedAllocation] : []),
    ...allocationTasks.filter(
      (t) =>
        t.carId === editState.primary.carId &&
        t.userId === editState.primary.userId
    ),
  ];
  const originDate = new Date(editState?.primary.startDate);
  const departureAllocation = allocations.find(
    (a) => a?.workCategory.id === getWorkCategoryIDByName('出発')
  );
  const workStartDate = departureAllocation
    ? new Date(departureAllocation.startDateForPlacement)
    : parseTime('00:00', originDate);
  const arrivalAllocation = allocations.find(
    (a) => a?.workCategory.id === getWorkCategoryIDByName('到着')
  );
  const workEndDate = arrivalAllocation
    ? new Date(arrivalAllocation.endDateForPlacement)
    : parseTime('24:00', originDate);
  const onNext = () => {
    if (!editState || !pairedAllocation) {
      return;
    }

    if (editState.isMoving) {
      dispatch(
        setEditTask({
          ...editState,
          secondary: pairedAllocation,
        })
      );
      dispatch(calcTasksReadyToUpdate());
      dispatch(removeEditTask());
      return;
    }

    dispatch(
      setEditTask({
        ...editState,
        secondary: pairedAllocation,
        step: editState.step === 2 ? 3 : 4,
      })
    );
  };
  const onBack = () => {
    if (!editState) {
      return;
    }

    if (editState.isMoving) {
      if (editState.step === 3) {
        dispatch(setEditTask({ ...editState, step: 2 }));
      } else {
        dispatch(removeEditTask());
      }

      return;
    }

    dispatch(
      setEditTask({
        ...editState,
        // この画面の step は2の場合と3の場合がある。型制約のため計算ではなく分岐で対応
        step: editState.step === 2 ? 1 : 2,
      })
    );
  };
  const onNextLabel = editState.isMoving ? '確定' : '次へ';

  const { allowClickAddButtonBefore, allowClickAddButtonAfter } =
    editState.primary.workCategory?.name === '回収' ||
    editState.primary.workCategory?.name === '積込'
      ? {
          allowClickAddButtonBefore: undefined,
          allowClickAddButtonAfter: new Date(editState.primary.startDate),
        }
      : {
          allowClickAddButtonBefore: new Date(editState.primary.endDate),
          allowClickAddButtonAfter: undefined,
        };

  return (
    <div className={styles.container}>
      <div className={styles.scrollContainer}>
        <Head />
        <div className={styles.contents}>
          <div>付随する作業: {editState?.secondary.workCategory?.name}</div>
          <div className={styles.timelineContainer}>
            <div className={styles.timeline}>
              <TimelineHead />
            </div>
            <div className={classNames(styles.timeline)}>
              {times.map((time) => (
                <Cell key={time}>
                  <TimeCellData
                    currentTime={parseTime(time, originDate)}
                    allocations={allocations}
                    workStartTime={workStartDate}
                    workEndTime={workEndDate}
                    onClickAddButton={() =>
                      setSelectedTime(parseTime(time, originDate))
                    }
                    allowClickAddButtonBefore={allowClickAddButtonBefore}
                    allowClickAddButtonAfter={allowClickAddButtonAfter}
                    targetAllocation={editState.primary}
                    pairedTargetAllocation={
                      pairedAllocation ?? editState.secondary
                    }
                  />
                </Cell>
              ))}
            </div>
          </div>
        </div>
      </div>
      <ModalFooter
        onNext={onNext}
        onBack={onBack}
        onNextLabel={onNextLabel}
        disabledOnNext={!selectedTime}
      />
    </div>
  );
};

const Cell: FC<{ children: ReactNode }> = ({ children }) => {
  return <div className={styles.cell}>{children}</div>;
};

const Head = () => {
  return (
    <div className={styles.header}>
      付随する作業の開始時間を選択してください
    </div>
  );
};

export default AllocatePairTask;
